import React, { useState, useEffect, useRef } from 'react';
import { Columns, ChevronDown, Eye, EyeOff, RotateCcw } from 'lucide-react';

/**
 * Dropdown that lets the user toggle individual table columns on or off.
 * Persistence is owned by the parent through `useHiddenColumns`, so this
 * component is purely presentation + interaction.
 *
 * Props
 *   columns : Array<{ key: string; label: string }> -- togglable columns
 *   hidden  : Set<string>                           -- currently-hidden keys
 *   onToggle(key) : flip visibility of one column
 *   onReset()     : show all
 *   onResetOrder(): optional; clears the user's drag-to-reorder layout
 *                   for this board back to the board's default order.
 *                   When omitted the menu item is hidden, so legacy
 *                   callers that don't use `useColumnOrder` are unaffected.
 *   testIdSuffix  : optional, used to namespace data-testid attributes
 */
const ColumnToggle = ({ columns, hidden, onToggle, onReset, onResetOrder, testIdSuffix = '' }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const hiddenCount = hidden.size;

  return (
    <div ref={containerRef} className="relative inline-block" data-testid={`column-toggle${testIdSuffix ? `-${testIdSuffix}` : ''}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded transition-colors text-sm"
        data-testid={`column-toggle-btn${testIdSuffix ? `-${testIdSuffix}` : ''}`}
      >
        <Columns className="w-4 h-4 text-[#5B8DB8]" />
        <span>Columns{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {open && (
        <div
          className="absolute z-30 mt-1 w-64 bg-white border border-gray-200 rounded shadow-lg overflow-hidden"
          data-testid="column-toggle-menu"
        >
          <div className="max-h-80 overflow-y-auto">
            {columns.map((col) => {
              const isHidden = hidden.has(col.key);
              return (
                <button
                  key={col.key}
                  type="button"
                  onClick={() => onToggle(col.key)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 text-left"
                  data-testid={`column-toggle-item-${col.key}`}
                >
                  <span className={`flex items-center gap-2 ${isHidden ? 'text-gray-400' : 'text-gray-700'}`}>
                    {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4 text-[#5B8DB8]" />}
                    <span className={isHidden ? 'line-through' : ''}>{col.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {(hiddenCount > 0 || onResetOrder) && (
            <div className="border-t border-gray-200 bg-gray-50">
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => { onReset(); setOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#5B8DB8] hover:bg-gray-100 transition-colors"
                  data-testid="column-toggle-reset"
                >
                  <RotateCcw className="w-4 h-4" />
                  Show all columns
                </button>
              )}
              {onResetOrder && (
                <button
                  type="button"
                  onClick={() => { onResetOrder(); setOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#5B8DB8] hover:bg-gray-100 transition-colors border-t border-gray-200"
                  data-testid="column-toggle-reset-order"
                  title="Restore the default column order (clears your drag-to-reorder layout)"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset column layout
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColumnToggle;
